import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { useDialogs } from '../../../components/ui/DialogProvider';
import { Select } from '../../../components/ui/Select';
import { actionErrorText, api } from '../../../lib/api';
import { useCopySources } from '../../brand/queries';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { COMPANY_VOICE_KEYS, isEmptyValue, type CompanyVoiceKey } from '../draft/brandDraft';
import { COPY_FROM_COPY } from '../wizardCopy';

/**
 * Copy from another workspace, above step 2's fields (spec:150-153; web.md §5.6): the other workspaces
 * the person can reach, and Copy. Copy fills Core values, Mission and Vision with that workspace's text
 * as a draft (nothing is saved until Next), asking first when any of the three already has text. A
 * notice then says it is a copy, with Undo until the person moves on from the step. Hidden when there
 * is no other workspace.
 */
export function CopyFromWorkspace({ tenantId, disabled = false }: { tenantId: string; disabled?: boolean }) {
  const { state, dispatch } = useBrandDraft();
  const sources = useCopySources(tenantId);
  const dialogs = useDialogs();
  const selectId = useId();
  const [chosen, setChosen] = useState('');
  const [copying, setCopying] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The draft as it is when an answer comes back (the person may type while the copy loads).
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  }, [state]);

  const list = sources.data ?? [];
  if (list.length === 0) return null;

  const copy = async () => {
    const source = list.find((s) => s.tenantId === chosen);
    if (!source) return;
    setInfo(null);
    setError(null);
    setCopying(true);
    let values: Record<CompanyVoiceKey, string | null>;
    let name = source.name;
    try {
      const voice = await api.getCompanyVoiceForCopy(source.tenantId);
      values = { coreValues: voice.coreValues, mission: voice.mission, vision: voice.vision };
      name = voice.name || source.name;
    } catch (err) {
      setError(actionErrorText(err, COPY_FROM_COPY.failed));
      setCopying(false);
      return;
    }
    setCopying(false);
    if (COMPANY_VOICE_KEYS.every((key) => isEmptyValue(values[key]))) {
      setInfo(COPY_FROM_COPY.nothing(name));
      return;
    }
    if (COMPANY_VOICE_KEYS.some((key) => !isEmptyValue(latest.current.fields[key].value))) {
      const replace = await dialogs.confirm({
        title: COPY_FROM_COPY.confirmTitle,
        message: COPY_FROM_COPY.confirmMessage(name),
        confirmLabel: COPY_FROM_COPY.confirmLabel,
        cancelLabel: COPY_FROM_COPY.cancelLabel,
      });
      if (!replace) return;
    }
    dispatch({ type: 'copyCompanyVoice', from: { tenantId: source.tenantId, name }, values });
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={selectId} className="mb-1 block text-sm font-medium text-ink-strong">
          {COPY_FROM_COPY.label}
        </label>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <Select
            id={selectId}
            value={chosen}
            placeholder={COPY_FROM_COPY.placeholder}
            disabled={disabled || copying}
            onChange={(e) => {
              setChosen(e.target.value);
              setInfo(null);
              setError(null);
            }}
          >
            {list.map((source) => (
              <option key={source.tenantId} value={source.tenantId}>
                {source.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            loading={copying}
            disabled={disabled || chosen === ''}
            onClick={() => void copy()}
          >
            {COPY_FROM_COPY.button}
          </Button>
        </div>
        {info && (
          <p role="status" className="mt-2 text-sm text-ink-muted">
            {info}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-2 text-sm text-red-600 dark:text-error-dark">
            {error}
          </p>
        )}
      </div>
      {state.copyUndo && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-xl border border-teal-line bg-teal-tint px-4 py-3 text-sm text-ink sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="min-w-0">{COPY_FROM_COPY.copied(state.copyUndo.from.name)}</p>
          <Button
            type="button"
            variant="plain"
            size="sm"
            className="shrink-0 self-start sm:self-auto"
            disabled={disabled}
            onClick={() => dispatch({ type: 'undoCopy' })}
          >
            {COPY_FROM_COPY.undo}
          </Button>
        </div>
      )}
    </div>
  );
}
